'use client'
import { FC } from 'react'
import { testIds } from '@/root/shared-consts'
import cn from 'classnames'
import { InventoryItem, CapacityTableColumns } from '@/root/shared-types'
import { getDepartureRowClick, getDepartureRowModel } from '@/root/modules/shared/SelectDateTimePage'
import { AvailabilityCircle } from '@/root/modules/tuuleliinid/SelectDateTimePage/components/AvailabilityCircle'
import { useGetTranslations } from '@/root/shared-hooks'
import { DangerGoodsIcon } from '@/icons/DangerGoodsIcon'
import { AnchorIcon } from '@/icons/AnchorIcon'
import { PassengerIcon } from '@/icons/PassengerIcon'
import { BicycleIcon } from '@/icons/BicycleIcon'
import { CarIcon } from '@/icons/CarIcon'
import { LocalPassengerIcon } from '@/root/ui/shared'
import { SoldOutIcon } from '@/root/ui/tuuleliinid/icons/SoldOutIcon'

interface DepartureRowProps {
  item: InventoryItem
  isSelected: boolean
  showColumns: CapacityTableColumns
  onSelect: () => void
  showDivider: boolean
  gridTemplate: string
  insufficientCapacity?: boolean
  onInsufficientCapacityClick?: () => void
  disabled?: boolean
  soldOutText?: string
}

export const DepartureRow: FC<DepartureRowProps> = ({
  item,
  isSelected,
  showColumns,
  onSelect,
  showDivider,
  gridTemplate,
  insufficientCapacity = false,
  onInsufficientCapacityClick,
  disabled = false,
  soldOutText,
}) => {
  const { getTranslation } = useGetTranslations()

  const { isCancelled, isClosed, dangerousGoods, passengerData, residentData, bicycleData, carData, isSoldOut } =
    getDepartureRowModel(item, showColumns)
  const { isDisabled, handleClick } = getDepartureRowClick({
    isCancelled,
    isClosed,
    disabled,
    insufficientCapacity,
    onSelect,
    onInsufficientCapacityClick,
  })

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={cn('relative z-10 w-full', {
        'cursor-not-allowed': isDisabled || insufficientCapacity,
      })}
    >
      {/* Desktop/Tablet Layout */}
      <div
        className={cn(gridTemplate, 'hidden h-14 items-center rounded-lg md:grid', {
          'bg-red-50': isCancelled,
          'bg-neutral-normal-lighter': insufficientCapacity,
        })}
      >
        {/* Time */}
        <div className="flex items-center justify-center">
          <div
            data-testid={testIds.sailSelect.departureTime}
            className={cn('text-2xl leading-none font-bold uppercase transition-colors duration-300', {
              'text-white': isSelected,
              'text-neutral-medium-light': insufficientCapacity,
              'text-neutral-darkest': !isSelected && !isCancelled && !insufficientCapacity,
            })}
          >
            {item.departureTime}
            {item.isNextDay && <span className="ms-1 text-sm">*</span>}
          </div>
        </div>

        {/* Status Icons */}
        <div className="flex w-24 items-center justify-center gap-2">
          {dangerousGoods && (
            <span data-testid={testIds.sailSelect.dangerGoodsIcon}>
              <DangerGoodsIcon width={24} />
            </span>
          )}
          {isCancelled && (
            <span data-testid={testIds.sailSelect.cancelledIcon}>
              <AnchorIcon width={24} />
            </span>
          )}
        </div>

        {/* Availability Counts */}
        <div className="flex justify-between">
          {passengerData && (
            <AvailabilityCircle
              amount={passengerData.amount ?? 0}
              total={passengerData.total ?? 100}
              isSelected={isSelected}
              insufficientCapacity={insufficientCapacity}
              testId={testIds.sailSelect.passengerAvailabilityAmount}
            />
          )}
          {residentData && (
            <AvailabilityCircle
              amount={residentData.amount ?? 0}
              total={residentData.total ?? 100}
              isSelected={isSelected}
              insufficientCapacity={insufficientCapacity}
              testId={testIds.sailSelect.passengerReservedAmount}
            />
          )}
          {bicycleData && (
            <AvailabilityCircle
              amount={bicycleData.amount ?? 0}
              total={bicycleData.total ?? 100}
              isSelected={isSelected}
              insufficientCapacity={insufficientCapacity}
              testId={testIds.sailSelect.bikeAvailabilityAmount}
            />
          )}
          {carData && (
            <AvailabilityCircle
              amount={carData.amount ?? 0}
              total={carData.total ?? 100}
              isSelected={isSelected}
              insufficientCapacity={insufficientCapacity}
              testId={testIds.sailSelect.carAvailabilityAmount}
            />
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div
        className={cn('flex rounded-lg p-4 md:hidden', {
          'bg-base-medium-light': isSelected,
          'bg-red-50': isCancelled && !isSelected,
          'bg-neutral-normal-lighter': insufficientCapacity,
        })}
      >
        <div className="flex flex-1 flex-col">
          {/* Time */}
          <div
            data-testid={testIds.sailSelect.departureTime}
            className={cn('mb-5 text-left text-3xl leading-none font-bold uppercase', {
              'text-white': isSelected,
              'text-neutral-medium-light': insufficientCapacity,
              'text-neutral-darkest': !isSelected && !insufficientCapacity,
            })}
          >
            {item.departureTime}
            {item.isNextDay && <span className="ms-1 text-sm">*</span>}
          </div>

          {/* Warnings */}
          <div className="flex flex-col gap-2 text-left">
            {dangerousGoods && (
              <div data-testid={testIds.sailSelect.noteAboutDangerous} className="flex items-center gap-4">
                <DangerGoodsIcon width={35} />
                <span className={cn('text-sm leading-tight', isSelected ? 'text-white' : 'text-neutral-darkest')}>
                  {getTranslation('dangerousGoods')}
                </span>
              </div>
            )}
            {isCancelled && (
              <div data-testid={testIds.sailSelect.noteAboutCancelled} className="flex items-center gap-4">
                <AnchorIcon width={24} />
                <span className={cn('text-sm leading-tight', isSelected ? 'text-white' : 'text-neutral-darkest')}>
                  {getTranslation('noteAboutCancelled')}
                </span>
              </div>
            )}
            {isSoldOut && (
              <div data-testid={testIds.sailSelect.noteAboutSoldOut} className="flex items-center gap-4">
                <SoldOutIcon width={35} />
                <span className={cn('text-sm leading-tight', isSelected ? 'text-white' : 'text-neutral-darkest')}>
                  {soldOutText ?? getTranslation('outOfStockPart1')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Availability with icons */}
        <div className="flex flex-wrap justify-end gap-8">
          {passengerData && (
            <div className="flex flex-col items-center justify-between gap-2">
              <AvailabilityCircle
                amount={passengerData.amount ?? 0}
                total={passengerData.total ?? 100}
                isSelected={isSelected}
                testId={testIds.sailSelect.passengerAvailabilityAmount}
              />
              <PassengerIcon width={16} height={30} color={isSelected ? 'white' : 'black'} />
            </div>
          )}
          {residentData && (
            <div className="flex flex-col items-center justify-between gap-2">
              <AvailabilityCircle
                amount={residentData.amount ?? 0}
                total={residentData.total ?? 100}
                isSelected={isSelected}
                testId={testIds.sailSelect.passengerReservedAmount}
              />
              <LocalPassengerIcon width={30} height={30} color={isSelected ? 'white' : 'black'} />
            </div>
          )}
          {bicycleData && (
            <div className="flex flex-col items-center justify-between gap-2">
              <AvailabilityCircle
                amount={bicycleData.amount ?? 0}
                total={bicycleData.total ?? 100}
                isSelected={isSelected}
                testId={testIds.sailSelect.bikeAvailabilityAmount}
              />
              <BicycleIcon width={35} height={22} color={isSelected ? 'white' : 'black'} />
            </div>
          )}
          {carData && (
            <div className="flex flex-col items-center justify-between gap-2">
              <AvailabilityCircle
                amount={carData.amount ?? 0}
                total={carData.total ?? 100}
                isSelected={isSelected}
                testId={testIds.sailSelect.carAvailabilityAmount}
              />
              <CarIcon width={35} height={17} color={isSelected ? 'white' : 'black'} />
            </div>
          )}
        </div>
      </div>

      {showDivider && (
        <div className="border-neutral-normal-lighter absolute right-5 bottom-0 left-5 hidden h-px border-t md:block" />
      )}
    </button>
  )
}
